<template>
  <div
    id="pulseSteps"
    :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
  >
    <!-- Mobile Layout -->
    <el-steps
      v-if="$mq === 'mobile' || $mq === 'tablet'"
      :active="periods.findIndex(el => el === activePeriod)"
      direction="vertical"
      align-center
    >
      <el-step
        v-for="(period, key) in periods"
        :key="key"

        :class="{
          'pulse1': pulseNumber === 1,
          'pulse2': pulseNumber === 2,
          'pulse3': pulseNumber === 3,
          'active': period === activePeriod
        }"
      >
        <div
          slot="description"
          class="stepItem"
          :class="{'active': period === activePeriod}"
        >
          <div class="title">
            {{ $t(`pulse.${period}`) }}
          </div>
          <div class="date">
            {{ pulse[period] }}
          </div>
        </div>
      </el-step>
    </el-steps>

    <!-- Desktop Layout -->
    <div
      v-else
      class="container"
    >
      <div
        v-for="(period, key) in periods"
        :key="key"
      >
        <div
          class="item"
          :class="{
            'pulse1': pulseNumber === 1,
            'pulse2': pulseNumber === 2,
            'pulse3': pulseNumber === 3,
            'active': period === activePeriod
          }"
        >
          <div v-if="period === 'start_date'">
            <el-button
              v-if="(user.is_staff || user.is_coach) && !pulse.start_date"
              class="actionBtn"
              size="medium"
              round
              :disabled="!canLaunch"
              @click="launchPulse"
            >
              Launch
            </el-button>
            <div v-else>
              <div class="title">
                {{ $t(`pulse.${period}`) }}
              </div>
              <div class="date">
                {{ pulse.start_date ? pulse.start_date : 'Not set' }}
              </div>
            </div>
          </div>

          <div v-else-if="period === 'close_date'">
            <el-button
              v-if="(user.is_staff || user.is_coach) && !pulse.close_date"
              class="actionBtn"
              size="medium"
              round
              :disabled="!pulse.start_date"
              @click="closePulse"
            >
              Close
            </el-button>
            <div v-else>
              <div class="title">
                {{ $t(`pulse.${period}`) }}
              </div>
              <div class="date">
                {{ pulse.close_date ? pulse.close_date : 'Not set' }}
              </div>
            </div>
          </div>

          <div v-else>
            <div class="title">
              Reminder
            </div>
            <div class="date">
              <el-date-picker
                v-if="(user.is_staff || user.is_coach)
                  && pulse.status !== 'closed'
                  && (!pulse[period] || pulse[period] > $moment().format('YYYY-MM-DD'))"
                v-model="pulse[period]"
                type="date"
                placeholder="Pick a day"
                clearable
                size="small"
                :picker-options="pickerOptions"
                value-format="yyyy-MM-dd"
                @change="updatePulse(period, ...arguments)"
              />
              <span v-else>
                {{ pulse[period] ? pulse[period] : 'Not set' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import { mapState, mapActions } from 'vuex';

export default {
  props: {
    pulse: {
      type: Object,
      default: null,
    },

    pulseNumber: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      periods: [
        'start_date', 'reminder1_date', 'reminder2_date', 'reminder3_date', 'close_date',
      ],
      pickerOptions: {
        firstDayOfWeek: 1,
        disabledDate(time) {
          return time.getTime() < Date.now();
        },
      },
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user.user,
    }),

    activePeriod() {
      if (!this.pulse.start_date) return 'start_date';
      return 'close_date';
    },

    canLaunch() {
      return Object.keys(this.pulse).length > 0 && this.pulse.status !== 'closed';
    },
  },

  methods: {
    ...mapActions('coachAlerts', [
      'alertChanged',
    ]),

    async launchPulse() {
      try {
        const response = await api.launchPulse(this.pulse.leader.uuid);

        this.$emit('updatePulse', response.data);
        this.alertChanged({ type: 'PulseLaunch', uuid: this.pulse.leader.uuid });
      } catch (err) {
        console.log('err');
        if (err.msg) {
          this.$message.error(err.msg);
        } else {
          this.$message.error('Pulse not launched. Please try again later');
        }
      }
    },

    async closePulse() {
      this.$confirm('Are you sure you want to close the Pulse? Participants will no longer be able to submit their surveys.', '', {
        confirmButtonText: 'Close Pulse',
        cancelButtonText: 'Go Back',
        roundButton: true,
      }).then(async () => {
        try {
          const response = await api.closePulse(this.pulse.leader.uuid);

          this.$emit('updatePulse', response.data);
        } catch (err) {
          console.log('err');
          if (err.msg) {
            this.$message.error(err.msg);
          } else {
            this.$message.error('Pulse not closed. Please try again later');
          }
        }
      }).catch(() => {
        this.leader.sub_group_uuid = this.teamBackup;
      });
    },

    async updatePulse(period, date) {
      if (this.pulse.leader) {
        try {
          const data = { };
          data[`pulse${this.pulseNumber}`] = {};
          data[`pulse${this.pulseNumber}`][period] = date;

          const response = await api.updatePulse(this.pulse.leader.uuid, data);

          this.$emit('updatePulse', response.data);
        } catch (err) {
          console.log('err', err);
          if (err.msg) {
            this.$message.error(err.msg);
          } else {
            this.$message.error('Pulse not updated. Please try again later');
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#pulseSteps {
  height: calc(100% - 33px);

  .container {
    height: 100%;

    > div {
      height: 20%;
      position: relative;

      .item {
        height: 100%;
        background: rgba(82, 82, 82, 0.5);
        text-align: center;
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        justify-content: center;

        .title {
          font-size: 12px;
          padding-bottom: 8px;
        }

        .date {
          font-weight: bold;
          font-size: 14px;
        }

        &.active {
          &.pulse1 {
            background: #f3c065;
          }

          &.pulse2 {
            background: #3aaace;
          }

          &.pulse3 {
            background: #b79eff;
          }
        }
      }

      &:not(:last-child) {
        &::after {
          position: absolute;
          bottom: -1px;
          width: 80%;
          left: 10%; // 10% - 80% - 10%
          content: '';
          border-bottom: 2px solid rgba(255, 255, 255, 0.3);
        }
      }
    }
  }

  .el-steps {
    .el-step {
      .stepItem {
        display: flex;
        justify-content: space-between;
        padding: 5px 10px;
        margin-bottom: 10px;
        font-size: 16px;
        line-height: 24px;
        position: relative;
        top: -8px;

        .date {
          font-weight: bold;
        }

        &.active {
          background: rgba(193, 193, 193, 0.2);
          padding: 5px 10px;
          color: var(--themeFontColor);
          border-radius: 30px;
        }
      }
    }
  }

  .actionBtn {
    background: transparent;
    color: var(--themeFontColor);
    border-color: var(--themeFontColor);
    width: 80%;

    &.is-disabled {
      background: rgba(255, 255, 255, 0.3);
      // color: #c5c5c5;
      // border-color: #c5c5c5;
    }
  }
}
</style>

<style lang="scss">
#pulseSteps {
  .el-steps {
    .el-step {
      .el-step__icon {
        background: #c4c4c4;
        border: none;

        .el-step__icon-inner {
          display: none;
        }
      }

      .el-step__line {
        top: calc(100% - 24px);
        bottom: 0;
      }

      .el-step__description {
        padding-right: 0;
      }

      &.active {
        .el-step__icon {
          background: rgba(0, 172, 226, 0.5);
        }
      }
    }
  }

  &.mobileMode {
    .el-steps {
      .el-step {
        .el-step__head {
          &.is-finish {
            color: #c4c4c4;
            border-color: #c4c4c4;
          }

          &.is-wait {
            color: #c4c4c4;
            border-color: #c4c4c4;
          }
        }

        .el-step__main {
          .el-step__description.is-finish {
            color: #c4c4c4;
          }

          .el-step__description.is-wait {
            color: #c4c4c4;
          }
        }

        &.active {
          &.pulse1 {
            .el-step__head {
              .el-step__icon {
                background: #f3c065;
              }
            }
          }

          &.pulse2 {
            .el-step__head {
              .el-step__icon {
                background: #3aaace;
              }
            }
          }

          &.pulse3 {
            .el-step__head {
              .el-step__icon {
                background: #b79eff;
              }
            }
          }
        }
      }
    }
  }

  .el-date-editor {
    width: 95%;

    input {
      border-radius: 40px;
    }
  }
}
</style>
